import { Box, Dialog, Slide, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Markdown } from 'lib/Markdown';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import React from 'react';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Throbber } from 'lib/Throbber';
import Axios from 'axios';
import { BaseRouteParams } from 'pages/routes';
import { useParams } from 'react-router';
import { TransitionProps } from '@mui/material/transitions';

const PREFIX = 'TermsAndConditions';

const classes = {
    link: `${PREFIX}link`,
    markdownContainer: `${PREFIX}markdownContainer`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.link}`]: {
        textDecoration: 'underline',
        fontWeight: 'bold',
        cursor: 'pointer',
        color: theme.palette.hint
    },

    [`& .${classes.markdownContainer}`]: {
        padding: theme.spacing(1),
        width: '100%',
        height: '100%',
        paddingBottom: theme.spacing(7),
        overflow: 'hidden auto'
    }
}));

const Transition = React.forwardRef(
    (
        props: TransitionProps & {
            children: React.ReactElement<any, any>;
        },
        ref: React.Ref<unknown>
    ) => <Slide direction="up" ref={ref} {...props} />
);

interface TermsAndConditionsProps {
    variant?: 'caption';
}

export const TermsAndConditions: React.FC<React.PropsWithChildren<TermsAndConditionsProps>> = ({
    variant
}) => {
    const { t } = useTranslation();

    const { merchantId } = useParams<BaseRouteParams>();
    const [termsOpen, setTermsOpen] = React.useState(false);
    const [privacyOpen, setPrivacyOpen] = React.useState(false);
    const [termsMarkdown, setTermsMarkdown] = React.useState<string | null>(null);
    const [privacyMarkdown, setPrivacyMarkdown] = React.useState<string | null>(null);
    const handleTermsClick = React.useCallback(() => setTermsOpen(true), []);
    const handlePrivacyClick = React.useCallback(() => setPrivacyOpen(true), []);
    const handleTermsClose = React.useCallback(() => setTermsOpen(false), []);
    const handlePrivacyClose = React.useCallback(() => setPrivacyOpen(false), []);
    React.useEffect(() => {
        async function loadMarkdown() {
            try {
                setTermsMarkdown(null);
                setPrivacyMarkdown(null);
                const now = Date.now();
                const terms = await Axios.get(
                    `${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/terms?t=${now}`
                );
                const privacy = await Axios.get(
                    `${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/privacy?t=${now}`
                );
                setTermsMarkdown(terms.status === 200 ? terms.data : t('AUTH_TERMS_TERMS_LOADING_FAILED'));
                setPrivacyMarkdown(
                    privacy.status === 200 ? privacy.data : t('AUTH_TERMS_PRIVACY_LOADING_FAILED')
                );
            } catch (err) {
                setTermsMarkdown(t('AUTH_TERMS_TERMS_LOADING_FAILED'));
                setPrivacyMarkdown(t('AUTH_TERMS_PRIVACY_LOADING_FAILED'));
            }
        }
        loadMarkdown();
    }, [merchantId, t]);
    return (
        <StyledBox>
            <Typography align="center" variant={variant} component="div">
                {t('BY_CONTINUING')}{' '}
                <span className={classes.link} onClick={handleTermsClick}>
                    {t('AUTH_TERMS_TERMS_AND_CONDITIONS')}
                </span>{' '}
                {t('AUTH_TERMS_AND')}{' '}
                <span className={classes.link} onClick={handlePrivacyClick}>
                    {t('AUTH_TERMS_PRIVACY_POLICY')}
                </span>
            </Typography>
            <Dialog
                fullScreen
                open={termsOpen}
                slots={{
                    transition: Transition
                }}
            >
                <MuiAppBar title={t('ABOUT_TERMS')} icon={Close} onBack={handleTermsClose} disabledMenu />
                {termsMarkdown === null && <Throbber text={t('GENERAL_LOADING')} />}
                {termsMarkdown !== null && (
                    <Box className={classes.markdownContainer}>
                        <Markdown source={termsMarkdown} />
                    </Box>
                )}
            </Dialog>
            <Dialog
                fullScreen
                open={privacyOpen}
                slots={{
                    transition: Transition
                }}
            >
                <MuiAppBar title={t('ABOUT_PRIVACY')} icon={Close} onBack={handlePrivacyClose} disabledMenu />
                {privacyMarkdown === null && <Throbber text={t('GENERAL_LOADING')} />}
                {privacyMarkdown !== null && (
                    <Box className={classes.markdownContainer}>
                        <Markdown source={privacyMarkdown} />
                    </Box>
                )}
            </Dialog>
        </StyledBox>
    );
};
